import React, { useEffect, useState, useCallback } from 'react';

/**
 * Housekeeping SLA Settings (ER-MOM-03) — hotelier sets the baseline room-turn
 * cleaning duration (minutes) per room type. A cleaning task that runs longer
 * than its room type's SLA escalates to the Head of Housekeeping.
 */
function HkSlaConfig({ token, selectedProperty }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!token || !selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/v1/pms/housekeeping/sla-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load SLA config'); return; }
      setRules((data.rules || []).map(r => ({
        room_type_id: r.room_type_id,
        room_type_name: r.room_type_name,
        room_type_code: r.room_type_code,
        sla_minutes: r.sla_minutes ?? '',
      })));
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [token, selectedProperty, headers]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (idx, value) => {
    setRules(prev => {
      const next = prev.map(r => ({ ...r }));
      next[idx].sla_minutes = value === '' ? '' : Math.max(0, Number(value));
      return next;
    });
    setSuccess('');
  };

  const handleSave = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const res = await fetch('/api/v1/pms/housekeeping/sla-config', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: rules.map(r => ({ room_type_id: r.room_type_id, sla_minutes: r.sla_minutes })) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      setSuccess('Housekeeping SLA configuration saved.');
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSaving(false); }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Select a property to configure housekeeping SLAs.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>Housekeeping SLA Settings</h2>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Set the baseline room-turn cleaning time (minutes) per room type — e.g. Standard Room = 25 min.
        A cleaning task that runs past its SLA escalates to the <b>Head of Housekeeping</b>
        (numbers in Hotel Config → Alert Settings). Leave blank to disable SLA tracking for a room type.
      </p>

      {error && <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{success}</div>}

      {loading ? <p>Loading…</p> : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={thStyle}>Room Type</th>
                  <th style={thStyle}>Cleaning SLA (minutes)</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule, idx) => (
                  <tr key={rule.room_type_id}>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>
                      {rule.room_type_name}
                      {rule.room_type_code && <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>({rule.room_type_code})</span>}
                    </td>
                    <td style={tdStyle}>
                      <input type="number" min="0" value={rule.sla_minutes}
                        onChange={(e) => handleChange(idx, e.target.value)}
                        placeholder="e.g. 25" style={inputStyle} />
                    </td>
                  </tr>
                ))}
                {!rules.length && (
                  <tr><td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No room types found for this property.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 24px', background: saving ? '#999' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', borderBottom: '1px solid #e0e0e0' };
const inputStyle = { width: 160, padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, boxSizing: 'border-box' };

export default HkSlaConfig;
